import React from 'react';
import { useTranslation } from 'react-i18next';
import {
  Shield,
  User,
  Building,
  Calendar,
  Phone,
  UserCheck,
  Clock,
  Trash2,
  History
} from 'lucide-react';

const CompanyUsersCards = ({ users, onDelete, onOpenLoginActivity, onOpenTimeline, isUserManager, currentUserId, onToggleUserManager }) => {
  const { t } = useTranslation();

  const StatusBadge = ({ isActive }) => {
    return (
      <span className={`px-2 py-1 rounded-full border ${
        isActive 
          ? 'bg-green-100 text-green-800 border-green-200' 
          : 'bg-red-100 text-red-800 border-red-200'
      }`}>
        {isActive ? t('companyAdmins.active') : t('companyAdmins.inactive')}
      </span>
    );
  };

  const formatDate = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: '2-digit',
      month: 'short',
      day: 'numeric'
    });
  };

  const formatDateTime = (dateString) => {
    if (!dateString) return '—';
    return new Date(dateString).toLocaleDateString('en-US', {
      year: 'numeric',
      month: 'short',
      day: 'numeric',
      hour: '2-digit',
      minute: '2-digit'
    });
  };

  return (
    <div className="mt-6 space-y-4">
      {users.map((userItem) => (
        <div
          key={userItem.id}
          className="bg-gradient-to-r from-gray-50 to-blue-50 rounded-xl p-4 space-y-3 border border-gray-200 hover:shadow-md transition-shadow"
        >
          {/* User Header */}
          <div className="flex justify-between items-start">
            <div className="flex items-center gap-3">
              <div className="relative">
                <div className="w-12 h-12 bg-gradient-to-br from-blue-500 to-purple-600 rounded-full flex items-center justify-center text-white">
                  {userItem.user.firstName?.[0]}{userItem.user.lastName?.[0]}
                </div>
                <div className="absolute -bottom-1 -right-1 p-1 bg-white rounded-full border-2 border-white">
                  {userItem.role === 'admin' && <Shield size={12} className="text-red-600" />}
                  {userItem.role === 'candidate' && <User size={12} className="text-blue-600" />}
                  {userItem.role === 'employee' && <Building size={12} className="text-green-600" />}
                </div>
              </div>
              <div>
                <h3 className=" text-gray-900">
                  {userItem.user.firstName} {userItem.user.lastName}
                </h3>
                <p className=" text-gray-600">{userItem.user.email}</p>
              </div>
            </div>
            <div className="flex flex-col gap-2 items-end">
              <StatusBadge isActive={userItem.user.is_active} />
              {isUserManager && userItem.user.id !== currentUserId && (
                <div className="flex gap-1">
                  {onOpenTimeline && (
                    <button
                      type="button"
                      onClick={() => onOpenTimeline(userItem.user.id, `${userItem.user.firstName || ''} ${userItem.user.lastName || ''}`.trim())}
                      className="p-2 text-purple-600 hover:bg-purple-50 rounded-lg transition-colors border border-purple-100"
                      title={t('companyAdmins.viewTimeline', 'View timeline')}
                    >
                      <Clock size={16} />
                    </button>
                  )}
                  {onOpenLoginActivity && (
                    <button
                      type="button"
                      onClick={() => onOpenLoginActivity(userItem.user.id, `${userItem.user.firstName || ''} ${userItem.user.lastName || ''}`.trim())}
                      className="p-2 text-blue-600 hover:bg-blue-50 rounded-lg transition-colors border border-blue-100"
                      title={t('companyAdmins.viewLoginActivity', 'View login activity')}
                    >
                      <History size={16} />
                    </button>
                  )}
                  <button
                    onClick={() => onDelete(userItem.user.id, `${userItem.user.firstName} ${userItem.user.lastName}`)}
                    className="p-2 text-red-500 hover:bg-red-50 rounded-lg transition-colors border border-red-100"
                    title={t('companyAdmins.delete', 'Delete')}
                  >
                    <Trash2 size={16} />
                  </button>
                </div>
              )}
            </div>
          </div>

          {/* User Details */}
          <div className="space-y-2">
            {userItem.user.profile?.mobile && (
              <div className="flex items-center gap-2  text-gray-600">
                <Phone size={14} className="text-gray-400" />
                <span>{userItem.user.profile.country_code} {userItem.user.profile.mobile}</span>
              </div>
            )}

            {userItem.user.profile?.aadhaar_number && (
              <div className="flex items-center gap-2  text-gray-600">
                <UserCheck size={14} className="text-gray-400" />
                <span>{t('companyAdmins.aadhaar')} {userItem.user.profile.aadhaar_number}</span>
              </div>
            )}

            <div className="flex items-center gap-2  text-gray-600">
              <Calendar size={14} className="text-gray-400" />
              <span>{t('companyAdmins.joined')} {formatDate(userItem.user.date_joined)}</span>
            </div>

            <div className="flex items-center gap-2  text-gray-600">
              <Clock size={14} className="text-gray-400" />
              <span>{t('companyAdmins.lastLogin')} {userItem.user.last_login ? formatDateTime(userItem.user.last_login) : t('companyAdmins.never')}</span>
            </div>

            {userItem.user.is_staff && (
              <div className="flex items-center gap-2 ">
                <Shield size={14} className="text-orange-600" />
                <span className="text-orange-600 ">{t('companyAdmins.staffMember')}</span>
              </div>
            )}

            {userItem.is_usermanager && (
              <div className="flex items-center gap-2 ">
                <UserCheck size={14} className="text-blue-600" />
                <span className="text-blue-600 ">{t('companyAdmins.userManager')}</span>
              </div>
            )}
          </div>

          {/* Footer */}
          <div className="pt-2 border-t border-gray-200">
            <div className="flex justify-between items-center  text-gray-500">
              <span>{t('companyAdmins.added')} {formatDate(userItem.added_at)}</span>
              {userItem.added_by && (
                <span>{t('companyAdmins.byId')} {userItem.added_by}</span>
              )}
            </div>
          </div>
        </div>
      ))}
    </div>
  );
};

export default CompanyUsersCards;